import { Restore, Delete, Edit } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmButton } from 'components/Buttons';
import { useNotification } from 'hooks/useNotification';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDeleteIndividualMutation, useRestoreIndividualMutation } from 'gql';
import { useCurrentUserRoles } from 'hooks/useCurrentUserRoles';

type Individual = {
  id: number;
  isDeleted?: boolean;
};

interface Props {
  individual: Individual;
  onEdit?: () => void;
  hideDelete?: boolean;
}

export const IndividualActions: React.FC<Props> = ({ individual, onEdit, hideDelete }) => {
  const { formatMessage } = useIntl();
  const notifications = useNotification();
  const queryClient = useQueryClient();
  const { isAdministrator } = useCurrentUserRoles();

  const deleteIndividualMutation = useDeleteIndividualMutation({
    onSuccess: () => {
      queryClient.invalidateQueries(['findIndividuals']);
      queryClient.invalidateQueries(['findDeletedIndividuals']);
      notifications.notifySuccess(formatMessage({ id: 'Individual deleted successfully' }));
    }
  });

  const restoreIndividualMutation = useRestoreIndividualMutation({
    onSuccess: () => {
      queryClient.invalidateQueries(['findIndividuals']);
      queryClient.invalidateQueries(['findDeletedIndividuals']);
      notifications.notifySuccess(formatMessage({ id: 'Individual restored successfully' }));
    }
  });

  const onDeleteHandler = () => deleteIndividualMutation.mutate({ input: { id: individual.id } });
  const onRestoreHandler = () => restoreIndividualMutation.mutate({ input: { id: individual.id } });

  return (
    <>
      {onEdit && (
        <IconButton onClick={() => onEdit?.()}>
          <Edit />
        </IconButton>
      )}

      {(!hideDelete && !individual.isDeleted) && (
        <ConfirmButton
          buttonVariant='iconButton'
          outerButtonIcon={<Delete />}
          confirmText={formatMessage({ id: 'Deleted individuals will be moved to the Recycle Bin and can be restored later.' })}
          confirmTitle={formatMessage({ id: 'Delete individual' })}
          onConfirm={onDeleteHandler}
          loading={deleteIndividualMutation.isLoading}
          tooltip={formatMessage({ id: 'Delete' })}
        />
      )}

      {(individual.isDeleted && isAdministrator) && (
        <ConfirmButton
          buttonVariant='iconButton'
          outerButtonIcon={<Restore />}
          outerButtonText={formatMessage({ id: 'Restore' })}
          confirmText={formatMessage({ id: 'This will restore the individual and make it available in lookups again.' })}
          confirmTitle={formatMessage({ id: 'Restore individual' })}
          confirmButtonText={formatMessage({ id: 'Restore' })}
          confirmColor='info'
          onConfirm={onRestoreHandler}
          loading={restoreIndividualMutation.isLoading}
          tooltip={formatMessage({ id: 'Restore' })}
        />
      )}
    </>
  );
};

